import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDateInput } from "ui";

import Layout from "../components/Layout";
import { SuccessAndAcceptanceRates } from "../features/insights";
import { useAnalytics } from "../features/analytics/analyticsProvider";
import { format, subDays } from "date-fns";
import { useFeatureFlags } from "../hooks/query";
import { useRouter } from "next/router";
import { PageHeader } from "../components/PageHeading";
import { DatadogAnalytics } from "../features/insights/components/DatadogAnalytics";
import { useScrollSpy } from "../hooks/useScrollSpy";

const SECTION_TITLES: Record<string, string> = {
  overview: "Overview of your payments",
  revenue: "Revenue Overview",
  declines: "Decline overview",
  recovery: "Recovery overview",
  brands: "Brands overview",
};

export default function Dashboard() {
  const { featureFlags } = useFeatureFlags();
  const router = useRouter();
  const { query } = router;
  const { analytics } = useAnalytics();

  // Refs for section scrolling
  const overviewSectionRef = useRef<HTMLElement>(null);
  const revenueSectionRef = useRef<HTMLDivElement>(null);
  const declinesSectionRef = useRef<HTMLDivElement>(null);
  const recoverySectionRef = useRef<HTMLDivElement>(null);
  const brandsSectionRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<string>("overview");
  const [currentSectionTitle, setCurrentSectionTitle] = useState<string>(
    SECTION_TITLES.overview
  );
  const [isScrollSpyEnabled, setIsScrollSpyEnabled] = useState(true);

  const handleTabClick = useCallback((tab: string) => {
    setActiveTab(tab);
    setCurrentSectionTitle(SECTION_TITLES[tab]);
    setIsScrollSpyEnabled(false);

    const sectionMap: Record<
      string,
      React.RefObject<HTMLElement | HTMLDivElement>
    > = {
      overview: overviewSectionRef,
      revenue: revenueSectionRef,
      declines: declinesSectionRef,
      recovery: recoverySectionRef,
      brands: brandsSectionRef,
    };

    const targetRef = sectionMap[tab];
    if (targetRef?.current) {
      targetRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    setTimeout(() => {
      setIsScrollSpyEnabled(true);
    }, 800);
  }, []);

  // Memoize section refs array for scroll spy
  const sectionRefsArray = useMemo(
    () => [
      { id: "overview", ref: overviewSectionRef },
      { id: "revenue", ref: revenueSectionRef },
      { id: "declines", ref: declinesSectionRef },
      { id: "recovery", ref: recoverySectionRef },
      { id: "brands", ref: brandsSectionRef },
    ],
    []
  );

  useScrollSpy({
    sectionRefs: sectionRefsArray,
    onActiveChange: (activeId) => {
      setActiveTab(activeId);
      setCurrentSectionTitle(SECTION_TITLES[activeId]);
    },
    rootMargin: "-180px 0px -50% 0px",
    enabled: featureFlags.datadog_dashboard_analytics && isScrollSpyEnabled,
  });

  useEffect(() => {
    if (!featureFlags.new_dashboard_sr_and_ar) {
      const {
        start_date: _start_date,
        end_date: _end_date,
        organization: _organization,
        account: _account,
        ...rest
      } = query;

      router.replace({
        query: {
          ...rest,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags.new_dashboard_sr_and_ar]);

  useEffect(() => {
    const startDate = router.query.start_date;
    const endDate = router.query.end_date;
    if (!startDate || !endDate) return;

    const now = new Date();
    const today = format(now, useDateInput.valueFormatString);
    const lastWeek = format(subDays(now, 7), useDateInput.valueFormatString);
    const last30Days = format(subDays(now, 30), useDateInput.valueFormatString);

    const isTodaySelected = startDate === today && endDate === today;
    const isLastWeekSelected = startDate === lastWeek && endDate === today;
    const isLast30DaysSelected = startDate === last30Days && endDate === today;

    const preset = isTodaySelected
      ? "today"
      : isLastWeekSelected
        ? "last_week"
        : isLast30DaysSelected
          ? "last_30_days"
          : "custom";

    analytics.track("home_dashboard_viewed", {
      preset,
    });
  }, [router.query.start_date, router.query.end_date, analytics]);

  useEffect(() => {
    window.Appcues?.track("analytics_dashboard.viewed");
  }, []);

  return (
    <Layout metaTitle="Home">
      <PageHeader
        title="Home"
        description="Get access to an overview of your payment hub in one place."
        withoutBorder={featureFlags.datadog_dashboard_analytics}
      />

      {featureFlags.new_dashboard_sr_and_ar && (
        <SuccessAndAcceptanceRates
          ref={overviewSectionRef}
          activeTab={activeTab}
          onTabChange={handleTabClick}
          sectionTitle={currentSectionTitle}
        />
      )}

      <DatadogAnalytics
        revenueSectionRef={revenueSectionRef}
        declinesSectionRef={declinesSectionRef}
        recoverySectionRef={recoverySectionRef}
        brandsSectionRef={brandsSectionRef}
      />
    </Layout>
  );
}
